import {
  TicketStatus,
  TicketPriority,
  TicketChannel,
  TicketCategory,
  TicketNature,
  TicketSegment,
} from './models/Ticket';

export const STATUS_META: Record<TicketStatus, {label: string; color: string}> =
  {
    aberto: {label: 'Aberto', color: '#21AEFF'},
    em_andamento: {label: 'Em andamento', color: '#0378F2'},
    aguardando_cliente: {label: 'Aguardando cliente', color: '#1789A3'},
    resolvido: {label: 'Resolvido', color: '#28A745'},
    fechado: {label: 'Fechado', color: '#71717A'},
  };

// O board renderiza só estes 4, nesta ordem. `fechado` é uma visão separada (só leitura).
export const BOARD_STATUSES: TicketStatus[] = [
  'aberto',
  'em_andamento',
  'aguardando_cliente',
  'resolvido',
];

export const PRIORITY_META: Record<
  TicketPriority,
  {label: string; color: string}
> = {
  baixa: {label: 'Baixa', color: '#71717A'},
  media: {label: 'Média', color: '#E29400'},
  alta: {label: 'Alta', color: '#DC3545'},
  urgente: {label: 'Urgente', color: '#A1121F'},
};

export const CHANNEL_META: Record<
  TicketChannel,
  {label: string; icon: string}
> = {
  whatsapp: {label: 'WhatsApp', icon: 'bi-whatsapp'},
  ligacao: {label: 'Ligação', icon: 'bi-telephone'},
  email: {label: 'E-mail', icon: 'bi-envelope'},
  reclame_aqui: {label: 'Reclame Aqui', icon: 'bi-megaphone'},
  google_avaliacoes: {label: 'Google Avaliações', icon: 'bi-star'},
  marketplace_ml: {label: 'Mercado Livre', icon: 'bi-shop'},
  marketplace_magalu: {label: 'Magalu', icon: 'bi-shop'},
  plataforma: {label: 'Plataforma', icon: 'bi-grid'},
};

/**
 * Rótulo de TODA categoria legível — inclusive as que o backend ainda não aceita na
 * escrita. Os call sites de exibição (TicketDetail, unifiedView/mappers) fazem lookup
 * cru aqui: sem o rótulo, um ticket vindo do backend novo renderizaria `undefined`.
 * Ter o mapa completo desacopla os dois deploys (spec 2026-08-26 §9).
 *
 * Para o que o usuário pode ESCOLHER, use CATEGORY_OPTIONS.
 */
export const CATEGORY_LABEL: Record<TicketCategory, string> = {
  devolucao: 'Devolução',
  troca: 'Troca',
  erro_entrega: 'Entrega — etapa não identificada',
  atraso: 'Atraso',
  pagamento: 'Pagamento',
  reclamacao: 'Reclamação',
  suporte_tecnico: 'Suporte técnico',
  duvida: 'Dúvida',
  preventivo: 'Preventivo',
  furo_estoque: 'Furo de estoque',
  cancelamento: 'Cancelamento',
  entrega_incorreta_incompleta: 'Entrega incorreta ou incompleta',
  baixa_contestada: 'Baixa contestada pela oficina',
  // Rede para a ordem de deploy, não linha em produção: ver Ticket.ts.
  erro_expedicao: 'Erro de expedição',
  atraso_rota: 'Atraso na rota de entrega',
  problema_coleta: 'Problema na coleta',
  atraso_compras: 'Atraso em compras',
};

/**
 * Categorias oferecidas ao usuário (form de criação e popover de filtros), nesta ordem.
 *
 * Dois grupos, não mais uma cadeia só. Primeiro as que nomeiam a ETAPA onde o pedido
 * está parado (compras → coleta → rota): ali o elo é observável, porque quem está com o
 * pedido na mão é quem o tem. Depois as que nomeiam o que se CONSTATA na entrega —
 * "incorreta ou incompleta" e o balde "etapa não identificada" —, onde o ops vê o
 * sintoma e ainda não sabe o elo.
 *
 * A separação é o mesmo argumento que já justificava o balde: forçar granularidade que
 * o ops não tem na abertura produz chute, e chute converge para a opção mais plausível.
 * Foi por isso que `erro_expedicao` virou `entrega_incorreta_incompleta` (ADR-0025) — o
 * chute ali não ficava só impreciso, ele acusava a expedição por uma peça que podia ter
 * sido comprada errada. Os dois ficam no fim: precisam existir, sem ser o clique fácil.
 *
 * `atraso` e `preventivo` saíram: o primeiro ficou coberto por atraso_rota/atraso_compras,
 * o segundo virou o eixo `nature`. Nenhum dos dois sai de CATEGORY_LABEL — as linhas
 * antigas em produção continuam legíveis e filtráveis.
 */
export const CATEGORY_OPTIONS: TicketCategory[] = [
  'atraso_compras',
  'problema_coleta',
  'atraso_rota',
  'entrega_incorreta_incompleta',
  'baixa_contestada',
  'erro_entrega',
  'devolucao',
  'troca',
  'cancelamento',
  'furo_estoque',
  'pagamento',
  'reclamacao',
  'suporte_tecnico',
  'duvida',
];

/**
 * Preventivo × corretivo. Só o preventivo ganha selo na fila: marcar os dois seria
 * marcar nenhum — a maioria é corretiva, e o sinal útil é "este aqui é proativo".
 */
export const NATURE_META: Record<TicketNature, {label: string; color: string}> =
  {
    preventivo: {label: 'Preventivo', color: '#7E3AF2'},
    corretivo: {label: 'Corretivo', color: '#71717A'},
  };

export const SEGMENT_LABEL: Record<TicketSegment, string> = {
  bronze: 'Bronze',
  prata: 'Prata',
  ouro: 'Ouro',
  diamante: 'Diamante',
  select: 'Select',
  seguradora: 'Seguradora',
  marketplace: 'Marketplace',
};

/**
 * Categorias cujo ticket carrega itens do pedido — e os exige. Espelha
 * CATEGORIES_WITH_FURO_ITEMS do customer-support-serice (ADR-0023). A duplicação
 * é deliberada, no mesmo espírito de CATEGORY_LABEL: desacopla os dois deploys.
 */
export const CATEGORIES_WITH_FURO_ITEMS = [
  'furo_estoque',
  'cancelamento',
] as const;

export type FuroCategory = typeof CATEGORIES_WITH_FURO_ITEMS[number];

export const carriesFuroItems = (category: string): category is FuroCategory =>
  (CATEGORIES_WITH_FURO_ITEMS as readonly string[]).includes(category);

/** Todo texto do fluxo de itens que muda com a categoria. */
export const FURO_TEXTS: Record<
  FuroCategory,
  {
    titlePrefix: string;
    itemsLabel: string;
    detailLabel: string;
    orderError: string;
    itemsError: string;
    deferLabel: string;
    deferHint: string;
  }
> = {
  furo_estoque: {
    titlePrefix: 'Furo de estoque - PED-',
    itemsLabel: 'Itens em furo *',
    detailLabel: 'Itens em furo de estoque',
    orderError: 'Selecione o pedido do furo',
    itemsError: 'Selecione ao menos um item em furo',
    deferLabel: 'Abrir sem devolução agora (tratar o furo manualmente)',
    deferHint:
      'Registra o furo sem gerar devolução/estorno. Você abre a devolução depois, item a item, direto no ticket.',
  },
  cancelamento: {
    titlePrefix: 'Cancelamento - PED-',
    itemsLabel: 'Itens cancelados *',
    detailLabel: 'Itens cancelados',
    orderError: 'Selecione o pedido do cancelamento',
    itemsError: 'Selecione ao menos um item cancelado',
    deferLabel: 'Abrir sem devolução agora (tratar o cancelamento manualmente)',
    deferHint:
      'Registra o cancelamento sem gerar devolução/estorno. Você abre a devolução depois, item a item, direto no ticket.',
  },
};

/**
 * Prefixos de título gerados automaticamente. O autofill só sobrescreve títulos
 * que comecem com um deles — senão, trocar de categoria congelaria o título
 * antigo por parecer escrito à mão.
 */
export const FURO_TITLE_PREFIXES = CATEGORIES_WITH_FURO_ITEMS.map(
  (c) => FURO_TEXTS[c].titlePrefix
);
